import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Sparkles, Flower2 } from "lucide-react";
import { PROFILE } from "@/data/content";
import { MaskedLines, Pop } from "@/components/bits/Reveal";
import { Sticker, HandNote, Arrow, Tape } from "@/components/bits/Sticker";
import { CursorTag } from "@/components/bits/CursorTag";
import { scrollToId } from "@/hooks/useLenis";

export const Hero = ({ play }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-10, 10]);
  const farX = useTransform(sx, (v) => v * -40);
  const farY = useTransform(sy, (v) => v * -40);
  const nearX = useTransform(sx, (v) => v * 30);
  const nearY = useTransform(sy, (v) => v * 30);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yTitle = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden pt-28 pb-20"
      data-testid="hero-section"
    >
      <motion.div style={{ x: farX, y: farY }} className="absolute inset-0 pointer-events-none">
        <CursorTag label="Creative Producer" color="blue" className="left-[6%] top-[22%] hidden md:block" delay={1.3} play={play} float={10} />
        <CursorTag label="Story Producer" color="pink" className="right-[8%] top-[28%] hidden md:block" delay={1.5} play={play} float={14} />
        <CursorTag label="Mumbai · she/her" color="green" className="left-[12%] bottom-[24%] hidden lg:block" delay={1.7} play={play} float={8} />
        <CursorTag label="9 yrs on set" color="orange" className="right-[14%] bottom-[20%] hidden lg:block" delay={1.9} play={play} float={12} />
      </motion.div>

      <motion.div style={{ y: yTitle, opacity: fade }} className="relative z-10 text-center max-w-5xl px-6 w-full">
        <Pop play={play} delay={0.5} className="inline-block">
          <Sticker color="paper" rotate={-3} data-testid="hero-hire-sticker">
            <span className="dot-live text-[var(--green)]" /> Open to new work & solving problems
          </Sticker>
        </Pop>

        <div className="relative mt-14">
          <HandNote className="absolute -top-12 md:-top-14 left-1/2 -translate-x-[120%] text-2xl md:text-3xl whitespace-nowrap">my name is</HandNote>
          <Arrow className="absolute -top-14 left-1/2 -translate-x-[10%] w-16 text-[var(--ink-60)] hidden md:block" />

          <motion.div style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 900 }} className="relative inline-block">
            <motion.div
              className="absolute -inset-3 md:-inset-5 border-[1.5px] border-[var(--blue)]"
              initial={{ opacity: 0 }}
              animate={play ? { opacity: 1 } : {}}
              transition={{ delay: 1.2, duration: 0.4 }}
              aria-hidden
            >
              {["-top-1.5 -left-1.5", "-top-1.5 -right-1.5", "-bottom-1.5 -left-1.5", "-bottom-1.5 -right-1.5"].map((p) => (
                <span key={p} className={`absolute ${p} w-3 h-3 bg-white border-[1.5px] border-[var(--blue)]`} />
              ))}
              <span className="absolute -top-7 right-0 bg-[var(--blue)] text-white font-mono text-[0.6rem] tracking-[0.1em] px-2 py-0.5 rounded-sm">1440 × 96</span>
            </motion.div>
            <MaskedLines
              play={play}
              delay={0.25}
              lines={[PROFILE.firstName]}
              lineClass="pixel-title text-[20vw] md:text-[10rem] lg:text-[12rem] leading-[0.85] text-[var(--ink)]"
              className="font-pixel"
            />
          </motion.div>
        </div>

        <motion.div style={{ x: nearX, y: nearY }} className="pointer-events-none">
          <Pop play={play} delay={0.9} rotate={-6} className="absolute -left-28 xl:-left-40 top-[24%] hidden lg:block">
            <div className="relative polaroid w-20 md:w-24 !p-1.5 !pb-1.5 rounded-full overflow-hidden">
              <img src={PROFILE.portrait} alt="Anushka Purohit" className="rounded-full" />
            </div>
          </Pop>
          <Pop play={play} delay={1.0} rotate={7} className="absolute -right-28 xl:-right-40 top-[26%] hidden lg:block">
            <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-[var(--yellow)] border-[1.5px] border-[var(--ink)] hard-shadow flex items-center justify-center">
              <Tape rotate={12} className="!w-14 -top-3 -right-4" />
              <svg viewBox="0 0 100 100" className="w-1/2 h-1/2" fill="none" stroke="var(--ink)" strokeWidth="6" strokeLinecap="round" aria-hidden>
                <rect x="28" y="28" width="9" height="20" fill="var(--ink)" stroke="none" />
                <rect x="63" y="28" width="9" height="20" fill="var(--ink)" stroke="none" />
                <path d="M28 66 Q50 86 72 66" />
              </svg>
            </div>
          </Pop>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-3 mt-8 md:mt-10">
          <Pop play={play} delay={1.0} rotate={-2}><Sticker color="yellow">Creative Producer</Sticker></Pop>
          <Pop play={play} delay={1.1} rotate={2}><Sticker color="blue">Art Director</Sticker></Pop>
          <Pop play={play} delay={1.2} rotate={-1}><Sticker color="pink">Production Manager</Sticker></Pop>
          <Pop play={play} delay={1.3} rotate={3}><Sticker color="green">Story Producer</Sticker></Pop>
        </div>

        <MaskedLines
          play={play}
          delay={1.0}
          inView={false}
          className="mt-10 md:mt-12 font-body font-semibold text-2xl sm:text-3xl md:text-4xl leading-tight tracking-tight"
          lines={[
            <>I make room for the story <Sparkles className="inline w-6 h-6 md:w-8 md:h-8 text-[var(--blue)] -mt-2 wiggle" /></>,
            <>to find its shape. <Flower2 className="inline w-6 h-6 md:w-8 md:h-8 text-[var(--pink)] -mt-2 wiggle" /></>,
          ]}
        />

        <Pop play={play} delay={1.5} className="mt-10">
          <button className="btn-ink" onClick={() => scrollToId("contact")} data-testid="hero-contact-btn">
            <span className="w-3 h-3 rounded-sm bg-[var(--blue)] border border-white/50" /> Contact me
          </button>
        </Pop>
      </motion.div>

      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[0.62rem] tracking-[0.25em] uppercase text-[var(--ink-60)] flex items-center gap-3"
        initial={{ opacity: 0 }}
        animate={play ? { opacity: 1 } : {}}
        transition={{ delay: 2 }}
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>↓</motion.span> scroll · the set is this way
      </motion.div>
    </section>
  );
};
